// Reusable searchable single-select combobox used by Candidate Portal Dossier
// address / education / role fields. Also supports "Other" fallback where the
// consumer wants free-text entry when no option matches.
import { useEffect, useMemo, useRef, useState } from "react";
import { CaretDown, MagnifyingGlass, X } from "@phosphor-icons/react";

export default function SearchableSelect({
  options = [],           // array of strings OR {value, label}
  value = "",
  onChange,
  placeholder = "Select…",
  disabled = false,
  allowCustom = false,    // if true, user can type an unmatched value and hit Enter to keep it
  testid,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Normalise options to {value,label}
  const list = useMemo(() =>
    options.map((o) => (typeof o === "string" ? { value: o, label: o } : o)),
    [options]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((o) =>
      String(o.label).toLowerCase().includes(q) ||
      String(o.value).toLowerCase().includes(q)
    );
  }, [list, query]);

  const displayLabel = useMemo(() => {
    const match = list.find((o) => o.value === value);
    return match ? match.label : (value || "");
  }, [list, value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const commit = (opt) => {
    onChange?.(opt.value);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative" data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) { setOpen((o) => !o); setTimeout(() => inputRef.current?.focus(), 30); } }}
        aria-label={ariaLabel || placeholder}
        className="w-full text-left border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white flex items-center justify-between hover:border-slate-300 disabled:bg-slate-50 disabled:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500 transition"
        data-testid={testid ? `${testid}-trigger` : undefined}
      >
        <span className={displayLabel ? "text-slate-900" : "text-slate-400"}>
          {displayLabel || placeholder}
        </span>
        <CaretDown size={14} className="text-slate-400" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-hidden"
             data-testid={testid ? `${testid}-panel` : undefined}>
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  if (filtered.length > 0) { commit(filtered[0]); }
                  else if (allowCustom && query.trim()) { onChange?.(query.trim()); setQuery(""); setOpen(false); }
                  e.preventDefault();
                } else if (e.key === "Escape") { setOpen(false); }
              }}
              placeholder="Type to search…"
              className="flex-1 text-sm outline-none py-1"
              data-testid={testid ? `${testid}-search` : undefined}
            />
            {value && (
              <button type="button" onClick={() => { onChange?.(""); }} className="text-slate-400 hover:text-red-600" title="Clear">
                <X size={12} />
              </button>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="text-xs text-slate-400 px-3 py-2">
                No matches.{allowCustom && query.trim() ? ` Press Enter to use "${query.trim()}".` : ""}
              </div>
            )}
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => commit(o)}
                className={`w-full text-left text-sm px-3 py-1.5 hover:bg-sky-50 ${o.value === value ? "bg-sky-50 text-sky-700 font-medium" : "text-slate-700"}`}
                data-testid={testid ? `${testid}-opt-${o.value}` : undefined}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
